import { useState, useEffect, useRef } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { AddressForm } from '../AddressForm';
import { TimelineSelector } from '../TimelineSelector';
import type { Address, ProjectTimeline } from '@/types/firestore';

export function ProjectDetailsStep() {
  const {
    state,
    updateContact,
    goToStep,
    tenantConfig,
    trackEvent,
    isLoading: providerLoading,
    updateLead,
    generationStatus,
  } = useWidget();

  // Get tenant default country for address form
  const defaultCountry = tenantConfig?.widget?.default_country;
  const requireAddress = tenantConfig?.widget?.require_address !== false;
  const requireTimeline = tenantConfig?.widget?.require_timeline !== false;

  const [address, setAddress] = useState<Partial<Address>>(
    state.contact.address || { country: defaultCountry || 'US' }
  );
  const [timeline, setTimeline] = useState<ProjectTimeline | undefined>(state.contact.project_timeline);
  const [additionalInfo, setAdditionalInfo] = useState(state.contact.additional_info || '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // Generation is now triggered in VerifyCodeStep after credits check

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate address if required
    if (requireAddress) {
      if (!address.street?.trim()) errors.street = 'Street address is required';
      if (!address.city?.trim()) errors.city = 'City is required';
      if (!address.state) errors.state = 'State/Province is required';
      if (!address.zip?.trim()) {
        errors.zip = address.country === 'US' ? 'ZIP code is required' : 'Postal code is required';
      } else {
        // Validate ZIP/Postal format
        const zip = address.zip.replace(/\s/g, '');
        if (address.country === 'US' && !/^\d{5}(-\d{4})?$/.test(zip)) {
          errors.zip = 'Please enter a valid ZIP code';
        } else if (address.country === 'CA' && !/^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/.test(zip)) {
          errors.zip = 'Please enter a valid postal code (e.g., A1A 1A1)';
        }
      }
    }

    // Validate timeline if required
    if (requireTimeline && !timeline) {
      errors.timeline = 'Please select when you want to start';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the data to save
      const projectAddress = requireAddress ? (address as Address) : undefined;
      const projectTimeline = timeline;
      const projectAdditionalInfo = additionalInfo.trim() || undefined;

      // Update contact with project details (for local state)
      updateContact({
        address: projectAddress,
        project_timeline: projectTimeline,
        additional_info: projectAdditionalInfo,
      });

      trackEvent('project_details_submitted');

      // Update lead with project details
      try {
        await updateLead({
          projectTimeline,
          additionalInfo: projectAdditionalInfo,
          address: projectAddress,
        });
      } catch (err) {
        console.error('Failed to update lead with project details:', err);
        // Continue to results even if update fails
      }

      // Navigate to results
      goToStep('results');
    } catch (err) {
      console.error('Failed to save project details:', err);
      // Continue to results even on error
      goToStep('results');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show generation status indicator
  const isGenerating = generationStatus === 'generating';

  return (
    <div className="space-y-6">
      {/* Success Message */}
      <Card className="p-4 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-500 shrink-0" />
          <div>
            <p className="font-medium text-green-800 dark:text-green-400">Phone verified!</p>
            <p className="text-sm text-green-700 dark:text-green-500">
              Just a few more details and you'll see your design.
            </p>
          </div>
        </div>
      </Card>

      {/* Generation In Progress Indicator */}
      {isGenerating && (
        <Card className="p-3 bg-accent/5 border-accent/20">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-accent animate-pulse shrink-0" />
            <span className="text-muted-foreground">Your AI renovation design is being created...</span>
          </div>
        </Card>
      )}

      {/* Project Details Form */}
      <div className="space-y-5">
        {/* Address Form - shown if required */}
        {requireAddress && (
          <AddressForm
            value={address}
            onChange={setAddress}
            errors={{
              street: fieldErrors.street,
              city: fieldErrors.city,
              state: fieldErrors.state,
              zip: fieldErrors.zip,
            }}
            required={requireAddress}
            fixedCountry={defaultCountry}
          />
        )}

        {/* Timeline Selector - shown if required */}
        {requireTimeline && (
          <TimelineSelector
            value={timeline}
            onChange={setTimeline}
            error={fieldErrors.timeline}
            required={requireTimeline}
          />
        )}

        {/* Additional Info - collapsible */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAdditionalInfo ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Add notes or special requirements (Optional)
          </button>

          {showAdditionalInfo && (
            <Textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Any specific requirements, questions, or notes for the contractor..."
              className="min-h-[80px] resize-none"
              maxLength={500}
            />
          )}
        </div>
      </div>

      {/* Submit Button */}
      <Button
        className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
        size="lg"
        onClick={handleSubmit}
        disabled={isSubmitting || providerLoading}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          'See My Design'
        )}
      </Button>

      {/* Trust signals */}
      <p className="text-xs text-center text-muted-foreground">
        Your information is only used to contact you about your project and is never shared.
      </p>
    </div>
  );
}
